import * as R from 'ramda';
import Preload from 'react-preload';
import React from 'react';
import { Page, Box } from 'react-layout-components';
import { Route, BrowserRouter, withRouter, Switch, Link } from 'react-router-dom';
import { compose, mapProps } from 'recompose';

import 'skeleton-css/css/normalize.css';
import 'skeleton-css/css/skeleton.css';
import './App.css';

import { Column, Container, Row, TextAlignment } from './Components';

const debug = (s = 'DEBUG') =>
    mapProps((props: any) => {
        console.log(s, props);
        return props;
    });

const LandingPage: React.SFC = () => (
    <Container textAlign={TextAlignment.center}>
        <img
            src={`${process.env.PUBLIC_URL}/images/title-white.jpg`}
            style={{ maxWidth: '100%', maxHeight: '65vh' }}
        />
        <br />
        <Link to='/page/1'>
            <button>
                Open
            </button>
        </Link>
    </Container>
);

const toPage = R.pipe(
    R.prop<any, any>('match'),
    R.prop('params'),
    R.pick(['page']),
    R.mapObjIndexed(Number),
);

const imageUrl = (page: number) =>
    `${process.env.PUBLIC_URL}/images/pages/${page}`;

const enhance = compose(
    withRouter,
    mapProps(toPage),
    mapProps(({ page }: any) => ({
        background: R.includes(page, textPages)
            ? '#fff'
            : '#000',
        border: R.includes(page, textPages)
            ? 'none'
            : '5px solid #000',
        leftSrc: `${imageUrl(page)}/l.jpg`,
        rightSrc: `${imageUrl(page)}/r.jpg`,
    })),
);

const imageStyle = {
    maxWidth: '100%',
    maxHeight: '65vh',
    marginLeft: 'auto',
    marginRight: 'auto',
};

const Navigation = compose(
    withRouter,
    mapProps(toPage),
    mapProps(({ page }: any) => ({
        backLink: {
            label: page > 1
                ? 'Previous'
                : 'Cover',
            to: page > 1
                ? `/page/${page - 1}`
                : '/'
        },
        forwardLink: {
            label: page < 40
                ? 'Next'
                : 'About',
            to: page < 40
                ? `/page/${page + 1}`
                : '/about'
        },
    })),
)(({ backLink, forwardLink }: any) => (
    <Container>
        <Row>
            <Column
                textAlign={TextAlignment.right}
                columnClass='one-third'
            >
                <Link
                    to={backLink.to}
                    style={{
                        position: 'absolute',
                        left: 0,
                    }}
                >
                    <button>
                        {backLink.label}
                    </button>
                </Link>
            </Column>
            <Column
                textAlign={TextAlignment.center}
                columnClass='one-third'
            >
                <Link
                    to='/about'
                >
                    About
                </Link>
            </Column>
            <Column
                textAlign={TextAlignment.left}
                columnClass='one-third'
            >
                <Link
                    to={forwardLink.to}
                    style={{
                        position: 'absolute',
                        right: 0,
                    }}
                >
                    <button>
                        {forwardLink.label}
                    </button>
                </Link>
            </Column>
        </Row>
    </Container >
));

const textPages = [
    1,
    3,
    5,
    7,
    9,
    11,
    13,
    15,
    17,
    19,
    21,
    23,
    25,
    27,
    29,
    31,
    33,
    35,
    37,
    39,
];

const BookPage = enhance(({ leftSrc, rightSrc, background, border }: any) => (
    <Page>
        <Box fit column>
            <Box column flex={8}>
                <Container>
                    <Row>
                        <Column
                            textAlign={TextAlignment.center}
                            columnClass='one-half'
                            background={background}
                            border={border}
                        >
                            <img
                                style={imageStyle}
                                src={leftSrc}
                            />
                        </Column>
                        <Column
                            textAlign={TextAlignment.center}
                            columnClass='one-half'
                            background={background}
                            border={border}
                        >
                            <img
                                style={imageStyle}
                                src={rightSrc}
                            />
                        </Column>
                    </Row>
                </Container>
            </Box>
            <Box row flex='1 1 2rem'>
                <Navigation />
            </Box>
        </Box>
    </Page>
));

const AboutPage = () => (
    <Page>
        <Box fit column>
            <Box column flex={8}>
                <Container>
                    <Row>
                        <Column textAlign={TextAlignment.left} columnClass='sixteen'>
                            <div>
                                <h3>Alfred Young</h3>
                                <p>artist | author</p>
                                <p>alfredalexanderyoung@gmail.com</p>
                                <a target='_blank' href="https://alfredyoungart.com">alfredyoungart.com</a>
                            </div>
                        </Column>
                    </Row>
                </Container>
            </Box>
            <Box row flex='1 1 2rem'>
                <Container>
                    <Row>
                        <Column
                            textAlign={TextAlignment.right}
                            columnClass='one-half'
                        >
                            <Link
                                to='/page/40'
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                }}
                            >
                                <button>
                                    Previous
                                </button>
                            </Link>
                        </Column>
                        <Column
                            textAlign={TextAlignment.left}
                            columnClass='one-half'
                        >
                        </Column>
                    </Row>
                </Container >
            </Box>
        </Box>
    </Page>
);

const LoadingPage = () => (
    <Page>
        <Box fit column>
            <Container>
                <Row>
                    <Column textAlign={TextAlignment.left} columnClass='sixteen'>
                        <div>
                            <h3>Loading...</h3>
                        </div>
                    </Column>
                </Row>
            </Container>
        </Box>
    </Page>
);

export const App: React.SFC = () => (
    <Preload
        images={R.reduce(
            (acc, i) => [
                `${process.env.PUBLIC_URL}/images/pages/${i}/l.jpg`,
                `${process.env.PUBLIC_URL}/images/pages/${i}/r.jpg`,
                ...acc,
            ],
            [] as string[],
            R.range(1, 41),
        )}
        loadingIndicator={<LoadingPage />}
        mountChildren
        resolveOnError
    >
        <BrowserRouter>
            <Switch>
                <Route path='/' exact>
                    <LandingPage />
                </Route>
                <Route path='/page/:page'>
                    <BookPage />
                </Route>
                <Route path='/about' exact>
                    <AboutPage />
                </Route>
                <Route path='*'>
                    <div>
                        <h1>404</h1>
                        <h2>Page Not Found</h2>
                    </div>
                </Route>
            </Switch>
        </BrowserRouter>
    </Preload>
);
